import { logOperationalEvent, logWarn } from 'saddlebag-logger';

import {
  getConsumer,
  getConsentIdServiceUrl,
  getPurposeScopedIdServiceLegacyCmpUrl,
  getPurposeScopedIdServiceKetchCmpUrl,
  isKetchMode,
  isPurposeScopedIdServiceEnabled,
  isPurposeScopedIdExistingGDPRUsersEnabled,
  isPurposeScopedIdNonRegulatedMarketsEnabled,
} from './cmp-state';
import {
  COMPONENT,
  CONSENT_ID_RESOLVE_FAILURE,
  CONSENT_ID_RESOLVE_RETRY,
  CONSENT_ID_RESOLVE_RETRY_FAILURE,
  CONSENT_ID_RESOLVE_RETRY_SUCCESS,
  CONSENT_ID_RESOLVE_SUCCESS,
  PURPOSE_SCOPED_ID_REFRESH_SUCCESS,
  PURPOSE_SCOPED_ID_REFRESH_FAILURE,
  EXISTING_USER_MIGRATION_SUCCESS,
  NON_REGULATED_MARKET_TRACKING_SUCCESS,
} from './constants/events';
import { cookieExists, getMultiValueCookie } from './cookie-factory';
import {
  PREFERENCES,
  GDPR_COOKIE_NAME,
  LATEST_VERSION,
  PURPOSE_SCOPED_ID_VERSION,
  NON_REGULATED_MARKET_DEFAULTS,
  isRegulatedMarket,
  getVersion,
  getPreferencesFromCookie,
} from './isomorphic';

import type { TrackingPreferences } from './isomorphic';

// ============================================================================
// Types
// ============================================================================

/**
 * Request body for the legacy purpose-scoped ID refresh endpoint.
 */
interface LegacyRefreshRequest {
  trackingConsent: boolean;
  advertisingConsent: boolean;
}

/**
 * Request body for the Ketch purpose-scoped ID refresh endpoint.
 */
interface KetchRefreshRequest {
  swb_consent: string;
}

// ============================================================================
// Consent ID Resolution
// ============================================================================

const CONSENT_ID_TIMEOUT_MS = 5000;
const CONSENT_ID_JITTER_MIN_MS = 50;
const CONSENT_ID_JITTER_MAX_MS = 100;

function classifyConsentIdError(error: unknown): string {
  if (error instanceof DOMException && error.name === 'AbortError')
    return 'timeout';
  if (error instanceof TypeError) return 'network';
  if (error instanceof Error) {
    const match = /^HTTP (\d+)$/.exec(error.message);
    if (match) return `http_${match[1]}`;
  }
  return 'unknown';
}

function isTransientConsentIdError(error: unknown): boolean {
  const type = classifyConsentIdError(error);
  return (
    type === 'timeout' || type === 'network' || /^http_(429|5\d\d)$/.test(type)
  );
}

export async function resolveConsentId(): Promise<void> {
  await fetchConsentId(false);
}

async function fetchConsentId(isRetry: boolean): Promise<void> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), CONSENT_ID_TIMEOUT_MS);

  let fetchError: unknown;
  let succeeded = false;
  try {
    const response = await fetch(getConsentIdServiceUrl(), {
      method: 'POST',
      credentials: 'include',
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    succeeded = true;
    logOperationalEvent({
      eventName: CONSENT_ID_RESOLVE_SUCCESS,
      component: COMPONENT,
      consumer: getConsumer(),
    });
    if (isRetry) {
      logOperationalEvent({
        eventName: CONSENT_ID_RESOLVE_RETRY_SUCCESS,
        component: COMPONENT,
        consumer: getConsumer(),
      });
    }
  } catch (error) {
    fetchError = error;
  } finally {
    clearTimeout(timeoutId);
  }

  if (succeeded) {
    return;
  }

  const errorType = classifyConsentIdError(fetchError);

  if (!isRetry && isTransientConsentIdError(fetchError)) {
    logOperationalEvent({
      eventName: CONSENT_ID_RESOLVE_RETRY,
      component: COMPONENT,
      consumer: getConsumer(),
      errorType,
    });

    // Jitter before retry to avoid thundering-herd
    await new Promise<void>((resolve) => {
      setTimeout(
        resolve,
        CONSENT_ID_JITTER_MIN_MS + Math.random() * CONSENT_ID_JITTER_MAX_MS,
      );
    });

    await fetchConsentId(true);
    return;
  }

  logOperationalEvent({
    eventName: CONSENT_ID_RESOLVE_FAILURE,
    component: COMPONENT,
    consumer: getConsumer(),
    errorType,
  });
  if (isRetry) {
    logOperationalEvent({
      eventName: CONSENT_ID_RESOLVE_RETRY_FAILURE,
      component: COMPONENT,
      consumer: getConsumer(),
      errorType,
    });
  }
  logWarn({
    component: COMPONENT,
    message: `${CONSENT_ID_RESOLVE_FAILURE}: ${fetchError instanceof Error ? fetchError.message : 'Unknown error'}`,
  });

  throw fetchError;
}

// ============================================================================
// Debounce State
// ============================================================================

/**
 * Debounce delay in milliseconds.
 * Consent changes can fire setPreferences() multiple times in rapid succession
 * (e.g. 10 calls within 11ms). Only the final consent state matters, so we
 * debounce to collapse burst calls into a single request.
 *
 * Uses a leading-edge pattern: the first call fires immediately (so the request
 * is in-flight before any potential page navigation), and subsequent calls
 * within the window are collapsed. If later calls arrive with different args,
 * a trailing call fires after the window to ensure the final state is sent.
 */
export const DEBOUNCE_DELAY_MS = 150;

// Legacy CMP debounce state
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let pendingRequest: LegacyRefreshRequest | null = null;

// Ketch CMP debounce state (independent of legacy)
let ketchDebounceTimer: ReturnType<typeof setTimeout> | null = null;
let ketchPendingRequest: string | null = null;

// ============================================================================
// Public API
// ============================================================================

/**
 * Refreshes purpose-scoped ID by calling the legacy CMP endpoint.
 *
 * This function is called internally when consent changes (via setPreferences).
 * It is fire-and-forget - errors are logged but not thrown.
 *
 * Rapid successive calls use a leading-edge debounce: the first call fires
 * immediately (surviving page navigation via keepalive), and subsequent calls
 * within 150ms are collapsed. A trailing request fires after the window with
 * the latest value if any calls arrived during the cooldown period.
 *
 * Calls POST /g/purpose-scoped-id-service/identifiers/refresh-legacy with
 * trackingConsent and advertisingConsent.
 *
 * @param prefs - The user's full tracking preferences (from the stored cookie)
 */
export function refreshPurposeScopedId(prefs: TrackingPreferences): void {
  const request = buildRefreshRequest(prefs);

  // Leading edge: if no timer is running, fire immediately
  if (debounceTimer === null) {
    callRefreshEndpointAsync(request);
    pendingRequest = null;

    // Start the cooldown window
    debounceTimer = setTimeout(() => {
      debounceTimer = null;

      // If calls arrived during the window, fire the latest one
      if (pendingRequest !== null) {
        const trailingRequest = pendingRequest;
        pendingRequest = null;
        callRefreshEndpointAsync(trailingRequest);
      }
    }, DEBOUNCE_DELAY_MS);
  } else {
    // During cooldown: just capture the latest args for trailing fire
    pendingRequest = request;
  }
}

/**
 * Refreshes purpose-scoped ID by calling the Ketch CMP endpoint.
 *
 * This function is called from the Ketch event bridge when consent changes.
 * It is fire-and-forget - errors are logged but not thrown.
 *
 * Rapid successive calls use a leading-edge debounce: the first call fires
 * immediately (surviving page navigation via keepalive), and subsequent calls
 * within 150ms are collapsed. A trailing request fires after the window with
 * the latest value if any calls arrived during the cooldown period.
 *
 * Calls POST /g/purpose-scoped-id-service/identifiers/refresh with
 * the raw Ketch consent cookie value (swb_consent).
 * The service decodes and interprets the consent server-side.
 *
 * @param consentCookieValue - The raw Ketch _swb_consent_ cookie value
 */
export function refreshPurposeScopedIdKetch(consentCookieValue: string): void {
  // Leading edge: if no timer is running, fire immediately
  if (ketchDebounceTimer === null) {
    callKetchRefreshEndpointAsync(consentCookieValue);
    ketchPendingRequest = null;

    // Start the cooldown window
    ketchDebounceTimer = setTimeout(() => {
      ketchDebounceTimer = null;

      // If calls arrived during the window, fire the latest one
      if (ketchPendingRequest !== null) {
        const trailingRequest = ketchPendingRequest;
        ketchPendingRequest = null;
        callKetchRefreshEndpointAsync(trailingRequest);
      }
    }, DEBOUNCE_DELAY_MS);
  } else {
    // During cooldown: just capture the latest args for trailing fire
    ketchPendingRequest = consentCookieValue;
  }
}

// ============================================================================
// Internal Implementation
// ============================================================================

/**
 * Builds the request body from tracking preferences.
 *
 * @param prefs - The user's full tracking preferences
 * @returns The request body for the purpose-scoped ID refresh endpoint
 */
function buildRefreshRequest(prefs: TrackingPreferences): LegacyRefreshRequest {
  return {
    trackingConsent: prefs[PREFERENCES.INFORMATION] === true,
    advertisingConsent: prefs[PREFERENCES.ADVERTS] === true,
  };
}

/**
 * Internal async implementation that calls the endpoint.
 * Separated to allow proper error handling while keeping the public API sync.
 *
 * @param request - The request body
 */
async function callRefreshEndpointAsync(
  request: LegacyRefreshRequest,
): Promise<void> {
  try {
    await callLegacyEndpoint(request);

    // Success - log
    logOperationalEvent({
      eventName: PURPOSE_SCOPED_ID_REFRESH_SUCCESS,
      component: COMPONENT,
      consumer: getConsumer(),
      trackingConsent: String(request.trackingConsent),
      advertisingConsent: String(request.advertisingConsent),
      mode: 'legacy',
    });
  } catch (error) {
    // Failure - dual logging pattern (operational event + warn)
    logOperationalEvent({
      eventName: PURPOSE_SCOPED_ID_REFRESH_FAILURE,
      component: COMPONENT,
      consumer: getConsumer(),
      trackingConsent: String(request.trackingConsent),
      advertisingConsent: String(request.advertisingConsent),
      mode: 'legacy',
    });
    logWarn({
      component: COMPONENT,
      message: `${PURPOSE_SCOPED_ID_REFRESH_FAILURE}: ${error instanceof Error ? error.message : 'Unknown error'}`,
    });
  }
}

/**
 * Reset debounce state for both legacy and Ketch.
 * FOR TESTING ONLY - do not use in production code.
 */
export function resetDebounceState(): void {
  if (debounceTimer !== null) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  pendingRequest = null;

  if (ketchDebounceTimer !== null) {
    clearTimeout(ketchDebounceTimer);
    ketchDebounceTimer = null;
  }
  ketchPendingRequest = null;
}

/**
 * Calls the legacy CMP endpoint to refresh purpose-scoped ID.
 *
 * @param request - The request body
 */
async function callLegacyEndpoint(
  request: LegacyRefreshRequest,
): Promise<void> {
  const url = getPurposeScopedIdServiceLegacyCmpUrl();

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include', // Include cookies for Set-Cookie to work
    keepalive: true, // Survive page navigation for fire-and-forget
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }
}

/**
 * Internal async implementation that calls the Ketch endpoint.
 * Separated to allow proper error handling while keeping the public API sync.
 *
 * @param consentCookieValue - The raw Ketch consent cookie value
 */
async function callKetchRefreshEndpointAsync(
  consentCookieValue: string,
): Promise<void> {
  try {
    await callKetchEndpoint(consentCookieValue);

    // Success - log
    logOperationalEvent({
      eventName: PURPOSE_SCOPED_ID_REFRESH_SUCCESS,
      component: COMPONENT,
      consumer: getConsumer(),
      mode: 'ketch',
    });
  } catch (error) {
    // Failure - dual logging pattern (operational event + warn)
    logOperationalEvent({
      eventName: PURPOSE_SCOPED_ID_REFRESH_FAILURE,
      component: COMPONENT,
      consumer: getConsumer(),
      mode: 'ketch',
    });
    logWarn({
      component: COMPONENT,
      message: `${PURPOSE_SCOPED_ID_REFRESH_FAILURE}: ${error instanceof Error ? error.message : 'Unknown error'}`,
    });
  }
}

/**
 * Calls the Ketch CMP endpoint to refresh purpose-scoped ID.
 *
 * @param consentCookieValue - The raw Ketch consent cookie value
 */
async function callKetchEndpoint(consentCookieValue: string): Promise<void> {
  const url = getPurposeScopedIdServiceKetchCmpUrl();
  const request: KetchRefreshRequest = { swb_consent: consentCookieValue };

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include', // Include cookies for Set-Cookie to work
    keepalive: true, // Survive page navigation for fire-and-forget
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }
}

// ============================================================================
// Shared Guards
// ============================================================================

/**
 * Returns true if legacy (skyscanner CMP) provisioning is available for the given market.
 * Both migrateExistingUserTracking and provisionNonRegulatedMarketTracking use this as
 * their common precondition before applying their own specific guards.
 *
 * @param market - The user's market code
 * @returns true if provisioning can proceed, false otherwise
 */
function isLegacyProvisioningAvailable(market: string): boolean {
  return !isKetchMode() && isPurposeScopedIdServiceEnabled() && Boolean(market);
}

// ============================================================================
// Existing User Migration
// ============================================================================

/**
 * Migrates existing users with v3 GDPR cookies to purpose-scoped-id-service.
 *
 * This function:
 * 1. Guards: Returns early if Ketch mode, service disabled, or migration disabled
 * 2. Checks if user has a v3 GDPR cookie (not v4+, already provisioned via normal flow)
 * 3. Reads existing consent values from the cookie
 * 4. If user has consent (information=true OR adverts=true):
 *    - Calls the legacy purpose-scoped-id-service endpoint
 *
 * Call this function after initTrackingPreferences() when migrating existing users.
 * Fires on every page load for v3 users with consent - the cookie is not modified.
 *
 * @param market - The user's market code (e.g., 'US', 'UK')
 */
export function migrateExistingUserTracking(market: string): void {
  if (!isLegacyProvisioningAvailable(market)) {
    return;
  }

  // Guard: Requires purpose-scoped ID for existing GDPR users to be enabled
  if (!isPurposeScopedIdExistingGDPRUsersEnabled()) {
    return;
  }

  // Guard: No cookie exists
  if (!cookieExists(GDPR_COOKIE_NAME)) {
    return;
  }

  const gdprCookie = getMultiValueCookie(GDPR_COOKIE_NAME);
  const version = getVersion(gdprCookie);

  // Guard: Already migrated (v4+)
  if (version >= PURPOSE_SCOPED_ID_VERSION) {
    return;
  }

  // Guard: Not a valid v3 cookie
  if (version < LATEST_VERSION) {
    return;
  }

  // Read existing consent from cookie
  const prefs = getPreferencesFromCookie(gdprCookie, market);

  // Guard: No consent to migrate (both false)
  if (!prefs[PREFERENCES.INFORMATION] && !prefs[PREFERENCES.ADVERTS]) {
    return;
  }

  // Call purpose-scoped-id-service with existing consent
  refreshPurposeScopedId(prefs);

  // Log success
  logOperationalEvent({
    eventName: EXISTING_USER_MIGRATION_SUCCESS,
    component: COMPONENT,
    consumer: getConsumer(),
    trackingConsent: String(prefs[PREFERENCES.INFORMATION]),
    advertisingConsent: String(prefs[PREFERENCES.ADVERTS]),
    market,
  });
}

// ============================================================================
// Non-Regulated Market Tracking
// ============================================================================

/**
 * Provisions tracking IDs for users in non-regulated markets.
 *
 * In non-regulated markets, users have implicit consent (consent=true by default).
 * This function provisions tracking IDs for these users without requiring a GDPR cookie.
 *
 * This function:
 * 1. Guards: Returns early if Ketch mode, service disabled, or non-regulated tracking disabled
 * 2. Guards: Returns early if market is regulated (should use migrateExistingUserTracking instead)
 * 3. Guards: Returns early if user already has a v4+ cookie (already tracked)
 * 4. Calls purpose-scoped-id-service with consent=true for both information and adverts
 *
 * Call this function after initTrackingPreferences() for non-regulated market users.
 * Safe to call multiple times - will no-op if already tracked (v4+ cookie exists).
 *
 * Requires both enablePurposeScopedIdService AND enablePurposeScopedIdNonRegulatedMarkets to be true.
 *
 * @param market - The user's market code (e.g., 'US', 'JP')
 */
export function provisionNonRegulatedMarketTracking(market: string): void {
  if (!isLegacyProvisioningAvailable(market)) {
    return;
  }

  // Guard: Requires purpose-scoped ID non-regulated markets to be enabled
  if (!isPurposeScopedIdNonRegulatedMarketsEnabled()) {
    return;
  }

  // Guard: Only for non-regulated markets
  if (isRegulatedMarket(market)) {
    return;
  }

  // If the user has an explicit GDPR cookie (e.g. visited Privacy Centre and opted out),
  // respect those preferences reading the GDPR cookie rather than assuming implicit consent.
  let prefs: TrackingPreferences;
  if (cookieExists(GDPR_COOKIE_NAME)) {
    const gdprCookie = getMultiValueCookie(GDPR_COOKIE_NAME);
    prefs = getPreferencesFromCookie(gdprCookie, market);
  } else {
    // No GDPR cookie — non-regulated markets have implicit consent (true for both)
    prefs = {
      ...NON_REGULATED_MARKET_DEFAULTS,
      isGdprActiveInMarket: false,
    };
  }

  // Call purpose-scoped-id-service with the resolved consent values
  refreshPurposeScopedId(prefs);

  // Log success
  logOperationalEvent({
    eventName: NON_REGULATED_MARKET_TRACKING_SUCCESS,
    component: COMPONENT,
    consumer: getConsumer(),
    trackingConsent: String(prefs[PREFERENCES.INFORMATION]),
    advertisingConsent: String(prefs[PREFERENCES.ADVERTS]),
    market,
  });
}
